import { isPlatformServer, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';
import { filter } from 'rxjs';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import {
  DialogNameOpenableByName,
  DialogOptions,
  DialogsComponentOpenData,
  OnGetDataFunction,
} from '../../interfaces/dialog-data.interface';
import { debounce } from '../../lib/debounce';
import { getScrollbarWidth } from '../../lib/get-scrollbar-width';
import { sleep } from '../../lib/sleep';
import { disableBodyScroll, enableBodyScroll } from '../../lib/toggle-body-scroll';
import { PICFLOW_ANALYTICS } from '../../picflow-analytics';
import { DialogService } from '../../services/dialog.service';
import { ThemeService } from '../../services/theme.service';
import { SvgComponent } from '../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../shared/directives/button.directive';

@Component({
  selector: 'app-dialogs',
  templateUrl: './dialogs.component.html',
  styleUrls: [
    './dialogs.component.scss',
    '../../../styles/animations/fade-up-subtle.scss',
    '../../../styles/animations/fade-down.scss',
    '../../../styles/animations/fade-left.scss',
    '../../../styles/animations/scale-fade.scss',
    '../../../styles/animations/simple-fade.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, SvgComponent, NgTemplateOutlet],
})
export class DialogsComponent implements OnInit {
  private dialog = inject(DialogService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private elRef = inject(ElementRef);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private themeService = inject(ThemeService);

  /**
   * Numeric scrim opacity — must be a concrete value (not a nested CSS var) so
   * simple-fade keyframes resolve correctly and opacity remains after Angular
   * removes the enter animation class.
   */
  readonly backdropOpacity = computed(() => {
    if (this.themeService.lightTheme()) return 0.25;
    if (this.themeService.darkTheme()) {
      return 0.55;
    }
    return 0.25;
  });

  @ViewChild('placeholder', { read: ViewContainerRef, static: false })
  private viewRef: ViewContainerRef;
  componentRef = signal<ComponentRef<{ dialogName: string; onGetData: OnGetDataFunction }>>(undefined);
  dialogOpen = signal(false);
  options = signal<DialogOptions>({});
  data = {};
  openDialogResolve: (...args) => void;
  private suspended?: {
    view: ViewRef;
    componentRef: ComponentRef<{ dialogName: string; onGetData: OnGetDataFunction }>;
    resolve: (...args) => void;
    options: DialogOptions;
    data: unknown;
    component: DialogsComponentOpenData['component'];
    selector: string;
    minHeight: string;
  };
  private restoring = false;
  currentSelector: string;
  component: DialogsComponentOpenData['component'];
  maxHeight = signal<number>(undefined);
  isMobile = signal(ScreenSizes.screenSize() === 'mobile');
  minHeight = signal<string>(undefined);

  private x = this.dialog.openByName$.pipe(filter(Boolean)).subscribe(data => {
    this.openByName(data.name, data.data);
  });

  @HostListener('window:keyup', ['$event']) keyEvent(event: KeyboardEvent) {
    if (event.target['form']) return;
    if (event.key === 'Escape') this.close(undefined);
  }

  @HostListener('window:resize') resizeEvent() {
    this.maxHeight.set(this.getDialogHeight());
    const isMobile = ScreenSizes.screenSize(true) === 'mobile';
    if (this.isMobile() !== isMobile && this.dialogOpen()) this.setComponentDebounce();
    this.isMobile.set(isMobile);
    if (this.options()?.minHeight) {
      this.minHeight.set(Math.min(this.options().minHeight, window.innerHeight) + 'px');
    }

    if (this.dialogOpen() && this.options().noGreyBackground) {
      if (!isMobile) {
        enableBodyScroll('dialog');
        this.document.body.style.setProperty('--scrollbar-width', '0px');
      } else {
        disableBodyScroll('dialog'); // duplicate because context menu enables it on mobile
        this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      }
    }
  }

  private getDialogHeight() {
    if (typeof window === 'undefined') return -1;
    return window.innerHeight - 72;
  }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.maxHeight.set(this.getDialogHeight());
    this.dialog.dialog$.subscribe(async (params: DialogsComponentOpenData) => {
      if (!params) return;
      if (this.openDialogResolve && params.suspendCurrent) this.suspendCurrent();
      else if (this.openDialogResolve) await this.close(undefined);
      this.dialogOpen.set(true);
      this.data = params.data;
      this.options.set(params.options);
      if (this.options().minHeight) {
        this.minHeight.set(Math.min(this.options().minHeight, window.innerHeight) + 'px');
      } else {
        this.minHeight.set(undefined);
      }
      this.component = params.component;
      if (!this.options().noGreyBackground || ScreenSizes.screenSize(true) === 'mobile') {
        disableBodyScroll('dialog'); // duplicate because context menu enables it on mobile
        this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      }
      await sleep(0);
      if (!this.viewRef) await sleep(100);
      if (!this.viewRef) return console.warn('viewRef not defined');
      if (!this.options().noGreyBackground || ScreenSizes.screenSize(true) === 'mobile') {
        disableBodyScroll('dialog'); // duplicate because context menu enables it on mobile
        this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      }
      this.setComponent();
      this.currentSelector = this.componentRef()?.instance?.dialogName;
      if (!this.currentSelector) console.warn('Dialog has no name', this.componentRef()?.componentType?.name);
      this.dialog.dialogOpenInfo$.next({ name: this.currentSelector });
      this.analytics.track('open_dialog', false, {
        name: this.currentSelector,
        ...this.analytics.lastButtonClickedParams(),
      });
      this.openDialogResolve = params.resolve;
    });
    this.dialog.dialog$.subscribe(e => {
      if (e === undefined && this.dialogOpen() && !this.restoring) this.close(false);
    });
  }

  private setComponentDebounce = debounce(() => {
    if (!this.dialogOpen()) return;
    this.setComponent();
  }, 100);

  private async setComponent() {
    if (!this.viewRef) return;
    this.viewRef.clear();
    this.componentRef.set(this.viewRef.createComponent(this.component));
    this.componentRef().instance.onGetData?.(this.data, this.close.bind(this), this.openByName.bind(this));
    await sleep(0);
    const input: HTMLInputElement = this.elRef.nativeElement.querySelector('input');
    if (input) input.focus();
  }

  private suspendCurrent() {
    const view = this.viewRef.detach(0);
    this.suspended = {
      view,
      componentRef: this.componentRef(),
      resolve: this.openDialogResolve,
      options: this.options(),
      data: this.data,
      component: this.component,
      selector: this.currentSelector,
      minHeight: this.minHeight(),
    };
    this.openDialogResolve = undefined;
    this.componentRef.set(undefined);
  }

  private restoreSuspended() {
    const suspended = this.suspended;
    this.suspended = undefined;
    this.viewRef.clear();
    this.viewRef.insert(suspended.view);
    this.componentRef.set(suspended.componentRef);
    this.openDialogResolve = suspended.resolve;
    this.options.set(suspended.options);
    this.data = suspended.data;
    this.component = suspended.component;
    this.currentSelector = suspended.selector;
    this.minHeight.set(suspended.minHeight);
    this.dialogOpen.set(true);
    this.restoring = true;
    this.dialog.close();
    this.restoring = false;
  }

  async close(event: unknown, delay = 200) {
    if (!this.dialogOpen()) return;
    if (this.suspended) {
      this.dialog.dialogClosedInfo$.next({ name: this.currentSelector });
      this.analytics.track('close_dialog', false, {
        name: this.currentSelector,
        ...this.analytics.lastButtonClickedParams(),
      });
      this.openDialogResolve?.(event);
      this.openDialogResolve = undefined;
      this.restoreSuspended();
      return;
    }
    this.dialog.dialogClosedInfo$.next({ name: this.currentSelector });
    this.analytics.track('close_dialog', false, {
      name: this.currentSelector,
      ...this.analytics.lastButtonClickedParams(),
    });
    this.openDialogResolve?.(event);
    this.openDialogResolve = undefined;
    this.dialogOpen.set(false);
    const ref = this.componentRef()?.instance;

    this.dialog.close(); // mark the dialog as closed

    // On !mobile we need to wait for the animation to finish before re-enabling the body scroll
    if (ScreenSizes.screenSize() !== 'mobile') await sleep(delay);
    enableBodyScroll('dialog');
    this.document.body.style.setProperty('--scrollbar-width', '0px');
    if (ScreenSizes.screenSize() === 'mobile') await sleep(delay);

    if (ref !== this.componentRef()?.instance) return;
    this.componentRef()?.destroy();
    this.componentRef.set(undefined);
  }

  async openByName(name: DialogNameOpenableByName, data?: unknown) {
    switch (name) {
      case 'verified-domains': {
        const { VerifiedDomainsDialogComponent } = await import(
          '../../account/dialogs/verified-domains-dialog/verified-domains-dialog.component'
        );
        return this.dialog.open(VerifiedDomainsDialogComponent, data);
      }
      case 'add-custom-domain': {
        const { AddCustomDomainDialogComponent } = await import(
          '../../account/dialogs/add-custom-domain-dialog/add-custom-domain-dialog.component'
        );
        return this.dialog.open(AddCustomDomainDialogComponent, data);
      }
      case 'verify-custom-domain': {
        const { VerifyCustomDomainDialogComponent } = await import(
          '../../account/dialogs/verify-custom-domain-dialog/verify-custom-domain-dialog.component'
        );
        return this.dialog.open(VerifyCustomDomainDialogComponent, data);
      }
      case 'add-verified-domain': {
        const { AddVerifiedDomainDialogComponent } = await import(
          '../../account/dialogs/add-verified-domain-dialog/add-verified-domain-dialog.component'
        );
        return this.dialog.open(AddVerifiedDomainDialogComponent, data);
      }
      case 'verify-verified-domain': {
        const { VerifyVerifiedDomainDialogComponent } = await import(
          '../../account/dialogs/verify-verified-domain-dialog/verify-verified-domain-dialog.component'
        );
        return this.dialog.open(VerifyVerifiedDomainDialogComponent, data);
      }
      case 'share-gallery': {
        const { ShareGalleryDialogComponent } = await import(
          '../../gallery/dialogs/share-gallery-dialog/share-gallery-dialog.component'
        );
        return this.dialog.open(ShareGalleryDialogComponent, data);
      }
      case 'embed-gallery': {
        const { EmbedGalleryDialogComponent } = await import(
          '../../gallery/dialogs/embed-gallery-dialog/embed-gallery-dialog.component'
        );
        return this.dialog.open(EmbedGalleryDialogComponent, data);
      }
      case 'share-gallery-app': {
        const { ShareGalleryAppDialogComponent } = await import(
          '../../gallery/dialogs/share-gallery-app-dialog/share-gallery-app-dialog.component'
        );
        return this.dialog.open(ShareGalleryAppDialogComponent, data);
      }
      case 'share-gallery-qr-code': {
        const { ShareGalleryQrCodeDialogComponent } = await import(
          '../../gallery/dialogs/share-gallery-qr-code-dialog/share-gallery-qr-code-dialog.component'
        );
        return this.dialog.open(ShareGalleryQrCodeDialogComponent, data);
      }
      case 'box-auth': {
        const { BoxAuthDialogComponent } = await import(
          '../../account/dialogs/box-auth-dialog/box-auth-dialog.component'
        );
        return this.dialog.open(BoxAuthDialogComponent, data);
      }
      case 'guest-profile': {
        const { GuestProfileDialogComponent } = await import(
          '../../view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component'
        );
        return this.dialog.open(GuestProfileDialogComponent, data);
      }
      case 'checkout': {
        const { CheckoutDialogComponent } = await import(
          '../../account/dialogs/checkout-dialog/checkout-dialog.component'
        );
        return this.dialog.open(CheckoutDialogComponent, data);
      }
      case 'change-trial': {
        const { ChangeTrialDialogComponent } = await import(
          '../../account/dialogs/change-trial-dialog/change-trial-dialog.component'
        );
        return this.dialog.open(ChangeTrialDialogComponent, data);
      }
      case 'change-plan': {
        const { ChangePlanDialogComponent } = await import(
          '../../account/dialogs/change-plan-dialog/change-plan-dialog.component'
        );
        return this.dialog.open(ChangePlanDialogComponent, data);
      }
      case 'gallery-settings-menu': {
        const { GallerySettingsMenuComponent } = await import(
          '../../gallery/menus/gallery-settings-menu/gallery-settings-menu.component'
        );
        return this.dialog.open(GallerySettingsMenuComponent, data);
      }
      case 'feature-paywall': {
        const { FeaturePaywallDialogComponent } = await import(
          '../../account/dialogs/feature-paywall-dialog/feature-paywall-dialog.component'
        );
        return this.dialog.open(FeaturePaywallDialogComponent, data);
      }
      default: {
        // asert that type is never
        const unknown: never = name;
        console.warn('Unknown search filter type:', unknown);
        return '';
      }
    }
  }
}
